/**
 * Per-deal documents (register C3).
 *
 * Distinct from intake source files: those are the original package a deal
 * arrived in and are immutable evidence. These are files a person adds to a
 * deal afterwards — a rent roll that followed by email, a signed term sheet,
 * a photo set — and they attach to the loan directly rather than going back
 * through Intake, which was the only route until now.
 */

import api from '@/services/api'
import { toCamelCase } from '@/utils/transform'

export type DealDocument = {
  id: string
  loanId: string | null
  propertyId?: string | null
  ingestionId?: string | null
  filename: string
  contentType: string | null
  sizeBytes: number
  category: string
  version: number
  storageBackend?: string | null
  checksumSha256?: string | null
  extractedFields?: Record<string, unknown> | null
  extractionConfidence?: number | null
  uploadedBy: string | null
  notes: string | null
  createdAt: string
}

/** Mirrors the server's own ceiling, so an oversized file is refused before
 *  it is read rather than after a slow upload ends in a 413. */
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024

/** A document as the vault lists it: the row plus the deal it was filed
 *  under and the parties behind that deal. */
export type VaultDocument = DealDocument & {
  dealName: string | null
  dealNumber: string | null
  broker: string | null
  sponsor: string | null
  borrower: string | null
  stage: string | null
}

export type VaultGroup = {
  loanId: string | null
  dealName: string
  dealNumber: string | null
  broker: string | null
  sponsor: string | null
  borrower: string | null
  stage: string | null
  documents: VaultDocument[]
}

export type VaultQuery = {
  /** Matches filename, note, category, deal name, deal number and parties. */
  q?: string
  broker?: string
  sponsor?: string
  borrower?: string
  category?: string
  loanId?: string
  limit?: number
}

/** What a filing sweep did. */
export type FilingSummary = {
  filed: number
  linked: number
  skipped: number
  details: { kind: string; filename?: string; category?: string; loanId?: string | null }[]
}

const vaultParams = (query: VaultQuery) => ({
  q: query.q || undefined,
  broker: query.broker || undefined,
  sponsor: query.sponsor || undefined,
  borrower: query.borrower || undefined,
  category: query.category || undefined,
  loan_id: query.loanId || undefined,
  limit: query.limit,
})

export const documentsService = {
  listForLoan: (loanId: string) =>
    api
      .get('/documents', { params: { loan_id: loanId } })
      .then((r) => toCamelCase<DealDocument[]>(r.data)),

  upload: (loanId: string, file: File, opts?: { category?: string; notes?: string }) => {
    const form = new FormData()
    form.append('file', file)
    form.append('loan_id', loanId)
    if (opts?.category) form.append('category', opts.category)
    if (opts?.notes) form.append('notes', opts.notes)
    return api
      .post('/documents/upload', form)
      .then((r) => toCamelCase<DealDocument>(r.data))
  },

  downloadUrl: (documentId: string) => `/documents/${documentId}/download`,

  download: (documentId: string) =>
    api.get(`/documents/${documentId}/download`, { responseType: 'blob' }).then((r) => r.data),

  /** The whole vault, filed under the deal each document belongs to.
   *
   *  Everything the platform ingests is filed automatically, so this is the
   *  complete record rather than the subset somebody uploaded by hand. */
  vault: (query: VaultQuery = {}) =>
    api
      .get('/documents', { params: { ...vaultParams(query), group_by: 'deal' } })
      .then((r) => toCamelCase<{ groups: VaultGroup[]; total: number }>(r.data)),

  /** A flat list, same filters, for a search that should read as results
   *  rather than as folders. */
  search: (query: VaultQuery = {}) =>
    api
      .get('/documents', { params: vaultParams(query) })
      .then((r) => toCamelCase<VaultDocument[]>(r.data)),

  /** File anything ingested that has no document row yet. Filing happens on
   *  its own after every ingestion; this is for a vault that predates it. */
  refile: () => api.post('/documents/refile').then((r) => toCamelCase<FilingSummary>(r.data)),

  /** Record a person's review of a stored document (who and when are kept on
   *  it, the change on the deal's trail), or withdraw it with `null`. */
  setReviewStatus: (documentId: string, status: DocumentReviewChoice | null, note?: string) =>
    api
      .patch(`/documents/${documentId}/review-status`, { status, note: note ?? null })
      .then((r) => toCamelCase<DealDocument>(r.data)),
}

/** The review states a person may record (`DOCUMENT_REVIEW_STATUSES` on the server). */
export type DocumentReviewChoice = 'received' | 'pending' | 'approved' | 'rejected' | 'expired'
